import React, { createContext, useState, useContext, useEffect } from 'react';
import apiService from '../services/apiService';
import socketService from '../services/socketService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth on mount
    const storedToken = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      socketService.connect(storedToken);
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      console.log('🔐 Login attempt with credentials:', { username: credentials.username });
      const response = await apiService.login(credentials);
      console.log('📡 Login response:', response);

      if (response.success && response.token) {
        const { token: newToken, admin } = response;
        console.log('✅ Login successful, token received');
        console.log('👤 Admin user:', admin);

        // Save to state
        setToken(newToken);
        setUser(admin);

        // Save to localStorage
        localStorage.setItem('adminToken', newToken);
        localStorage.setItem('adminUser', JSON.stringify(admin));
        console.log('💾 Token and user saved to localStorage');

        // Connect socket
        socketService.connect(newToken);
        console.log('🔌 Socket connection initiated');

        return { success: true };
      } else {
        console.log('❌ Login failed:', response.message);
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return {
        success: false,
        message: error.message || 'An error occurred during login'
      };
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state
      setToken(null);
      setUser(null);

      // Clear localStorage
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');

      // Disconnect socket
      socketService.disconnect();
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
